// ═══════════════════════════════════════════════════════════════
// FURD MEP.Engine — Offline Data Sync
// ═══════════════════════════════════════════════════════════════
// Caches API responses in IndexedDB for offline access.
// Queues write operations and syncs when back online.

const DB_NAME = "mep-engine-offline";
const DB_VERSION = 1;
const CACHE_STORE = "api_cache";
const SYNC_QUEUE = "sync_queue";

// Cache TTL in ms (15 minutes)
const CACHE_TTL = 15 * 60 * 1000;

// API paths that should be cached for offline reading
const CACHEABLE_PATHS = [
  "/dashboard",
  "/projects",
  "/admin/settings",
  "/admin/rates",
  "/admin/deliverables",
  "/admin/stages",
  "/admin/templates",
  "/auth/me",
];

// Pattern matchers for project-scoped paths
const CACHEABLE_PATTERNS = [
  /^\/projects\/[^/]+$/, // project detail
  /^\/projects\/[^/]+\/scope$/,
  /^\/projects\/[^/]+\/pricing$/,
  /^\/projects\/[^/]+\/risk$/,
  /^\/projects\/[^/]+\/proposals$/,
  /^\/projects\/[^/]+\/files$/,
];

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        db.createObjectStore(CACHE_STORE, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(SYNC_QUEUE)) {
        db.createObjectStore(SYNC_QUEUE, { keyPath: "id", autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function shouldCache(path) {
  if (CACHEABLE_PATHS.some((p) => path === p || path.endsWith(p))) return true;
  if (CACHEABLE_PATTERNS.some((re) => re.test(path))) return true;
  return false;
}

// ─── Cache Operations ───

export async function cacheResponse(path, data) {
  try {
    const db = await openDB();
    const tx = db.transaction(CACHE_STORE, "readwrite");
    tx.objectStore(CACHE_STORE).put({
      key: path,
      data,
      timestamp: Date.now(),
    });
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
}

export async function getCachedResponse(path) {
  try {
    const db = await openDB();
    const tx = db.transaction(CACHE_STORE, "readonly");
    const request = tx.objectStore(CACHE_STORE).get(path);
    return new Promise((resolve) => {
      request.onsuccess = () => {
        const result = request.result;
        if (!result) return resolve(null);
        // Check TTL
        if (Date.now() - result.timestamp > CACHE_TTL) return resolve(null);
        resolve(result.data);
      };
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function clearCache() {
  try {
    const db = await openDB();
    const tx = db.transaction(CACHE_STORE, "readwrite");
    tx.objectStore(CACHE_STORE).clear();
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
    });
  } catch {
    return false;
  }
}

// ─── Sync Queue Operations ───

export async function queueOfflineWrite(method, path, data) {
  try {
    const db = await openDB();
    const tx = db.transaction(SYNC_QUEUE, "readwrite");
    tx.objectStore(SYNC_QUEUE).add({
      method,
      path,
      data,
      timestamp: Date.now(),
    });
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
    });
  } catch {
    return false;
  }
}

export async function getPendingWrites() {
  try {
    const db = await openDB();
    const tx = db.transaction(SYNC_QUEUE, "readonly");
    const request = tx.objectStore(SYNC_QUEUE).getAll();
    return new Promise((resolve) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

export async function clearPendingWrite(id) {
  try {
    const db = await openDB();
    const tx = db.transaction(SYNC_QUEUE, "readwrite");
    tx.objectStore(SYNC_QUEUE).delete(id);
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
    });
  } catch {
    return false;
  }
}

export async function clearAllPendingWrites() {
  try {
    const db = await openDB();
    const tx = db.transaction(SYNC_QUEUE, "readwrite");
    tx.objectStore(SYNC_QUEUE).clear();
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
    });
  } catch {
    return false;
  }
}

export { shouldCache };
